import { useEffect, useRef, useState } from "react";
import { useTexte, WORTLAUT } from "../../texte";
import { useUhrwechsel } from "../../hooks/useUhrwechsel";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import {
  useDeleteMessages,
  useMarkRead,
  useMembers,
  useMessages,
  useOlderMessages,
  useThreads,
  useTrip,
} from "../../hooks/queries";
import { ApiError, ohneNetz } from "../../api/client";
import { vergissOrt } from "../../lib/letzterChat";
import { merkeUebergabe } from "../../lib/highlightUebergabe";
import type { Member, Message } from "../../api/types";
import { dayLabel, sameDay } from "../../lib/format";
import { ausschnittAus, grundStil } from "../../lib/ausschnitt";
import { BackIcon, InfoIcon } from "../../components/Icons";
import { Netzstreifen } from "../../components/Netzstreifen";
import { Auswahlleiste, nachrichtenWorte } from "./Auswahlleiste";
import { rueckfrage } from "../../components/Rueckfrage";
import { useOnline } from "../../hooks/useOutbox";
import { Reiseumschalter } from "../map/Reiseumschalter";
import { useOutbox } from "../../hooks/useOutbox";
import { AntwortenKnopf } from "./Antworten";
import { Bubble } from "./Bubble";
import { Composer } from "./Composer";
import { Wartend } from "./Wartend";

/** Worauf sich die Nachricht bezieht, die gerade geschrieben wird. */
export interface Bezug {
  message: Message;
  /** Gesetzt, wenn es um genau ein Foto des Beitrags geht. */
  mediaId?: number;
}

/**
 * Ein Chat.
 *
 * Ohne `userId` ist das die Reise selbst: für einen Empfänger der Einzelchat
 * mit dem Reisenden, für den Absender sein Travel Broadcast. Mit `userId` der
 * Antwortverlauf eines Empfängers -- den kann nur der Absender öffnen, und
 * das Backend prüft das noch einmal selbst.
 */
export function ThreadPage() {
  const t = useTexte();
  const params = useParams();
  const navigate = useNavigate();
  const tripId = Number(params.tripId);
  const withUser = params.userId ? Number(params.userId) : undefined;

  /*
   * **Wer Uhrzeiten in der Uhr des Geraets zeigt, hoert auf die Uhr** (#73).
   * Der Rueckgabewert wird nicht gelesen; er aendert sich beim Zonenwechsel
   * und zeichnet damit den ganzen Verlauf neu -- Sprechblasen, Tagestrenner
   * und die wartenden Beitraege. Ein Haken in `Shell` reichte nicht bis
   * hierher: react-router gibt dem `<Outlet/>` dasselbe Element zurueck, und
   * React ueberspringt den Teilbaum.
   */
  useUhrwechsel();

  const trip = useTrip(tripId);
  const messages = useMessages(tripId, withUser);
  const older = useOlderMessages(tripId, withUser);
  const markRead = useMarkRead(tripId, withUser);
  // Der Name des Empfängers kommt aus der Teilnehmerliste, nicht aus den
  // Nachrichten: wer noch nie geantwortet hat, käme darin gar nicht vor.
  const members = useMembers(tripId, withUser !== undefined);
  // Nur aus dem Zwischenspeicher gelesen -- die Liste nebenan fragt ohnehin.
  const threads = useThreads();
  // Was in diesem Verlauf noch auf Netz wartet -- steht unten, vor der
  // Eingabezeile, als wäre es schon gesendet. Denn das ist es aus Sicht
  // dessen, der es geschrieben hat.
  const wartende = useOutbox(tripId, withUser);

  // Worauf sich das bezieht, was gerade geschrieben wird. Der Zustand sitzt
  // hier und nicht im Composer: gesetzt wird er an einer Sprechblase weiter
  // oben, und die beiden kennen einander nur über diese Seite.
  //
  // Er steht bei den anderen Hooks und nicht weiter unten bei seinem Gebrauch:
  // darunter stehen frühe Rückgaben, und ein Hook hinter einer davon liefe
  // nicht bei jedem Durchgang.
  const [bezug, setBezug] = useState<Bezug | null>(null);
  /**
   * Der Auswahlmodus zum Löschen (#30) — `null` heißt: aus.
   *
   * **Er liegt hier und nicht an der Sprechblase**: eine Auswahl über mehrere
   * Nachrichten kann keine einzelne kennen. Die Blase bekommt nur, was sie
   * selbst angeht.
   */
  const [auswahl, setAuswahl] = useState<Set<number> | null>(null);
  const loeschen = useDeleteMessages(tripId, withUser);
  const online = useOnline();
  const [loeschbefund, setLoeschbefund] = useState<string | null>(null);

  /**
   * Escape beendet die Auswahl — dieselbe Taste wie überall im Projekt.
   *
   * Und **ein Wechsel des Verlaufs auch**: eine Auswahl aus einem anderen
   * Chat wäre eine Liste von Kennungen, die hier nichts bedeuten.
   */
  useEffect(() => {
    setAuswahl(null);
    setLoeschbefund(null);
  }, [tripId, withUser]);

  useEffect(() => {
    if (auswahl === null) return;
    const taste = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setAuswahl(null);
    };
    window.addEventListener("keydown", taste, true);
    return () => window.removeEventListener("keydown", taste, true);
  }, [auswahl]);

  // Beim Wechsel des Chats fällt der Bezug weg -- eine Nachricht aus der
  // vorigen Reise wäre hier weder zu sehen noch zulässig.
  useEffect(() => setBezug(null), [tripId, withUser]);

  /**
   * Der Verlauf selbst -- gebraucht wird er nur zum Springen ans Ende.
   *
   * **Aus dem Betrieb:** „wenn der Bildschirm schon voll ist, oder man nach
   * oben gescrollt hat: die letzte abgesendete Nachricht ist nach dem
   * Absenden nicht sichtbar, weil die Nachrichten nicht nach oben scrollen."
   *
   * Getragen hat das bisher allein `flex-direction: column-reverse`: solange
   * man **unten** steht, hängt der Browser von selbst am neuesten Ende. Wer
   * hochgescrollt hat, steht aber nicht mehr unten -- und dann ist das
   * richtige Verhalten für eine *fremde* Nachricht (bleiben, wo man liest)
   * das falsche für die *eigene*. Deshalb springt nur das Senden.
   */
  const verlauf = useRef<HTMLDivElement>(null);

  /**
   * Ans neueste Ende springen.
   *
   * `scrollTop = 0` ist in einer umgekehrten Liste das **untere** Ende; nach
   * oben läuft die Zahl ins Minus.
   *
   * **Ohne Weichzeichnen, und das ist gemessen.** Mit `behavior: "smooth"`
   * schrieb eine Spur alle 50 ms dies mit:
   *
   * ```
   * -607 -599 -556 -474 -400 -233 -156 -65 -14 -56 -56 -56 …
   * ```
   *
   * Der weiche Lauf kommt bis −14 und wird dann auf −56 zurückgerissen: die
   * neue Blase trifft erst mit der Antwort des Servers ein, die
   * Scrollverankerung schiebt um deren Höhe -- und eine laufende Animation
   * verliert gegen diese Verschiebung. Ein gesetzter Wert verliert nicht.
   */
  function springen() {
    const kasten = verlauf.current;
    if (kasten !== null) kasten.scrollTop = 0;
  }

  /**
   * Ein Sprung ist gewünscht -- aber einer reicht nicht.
   *
   * **Gemessen, nicht vermutet:** ein Sprung allein beim Absenden landete
   * daneben, um genau eine Blasenhöhe (Versatz −55 bei einer Zeile von 53).
   * Der Grund ist die Scrollverankerung des Browsers: die neue Blase kommt
   * *nach* dem Sprung dazu, und der Browser hält den sichtbaren Inhalt an
   * seinem Platz, indem er den Versatz um deren Höhe nachzieht. Der Sprung war
   * also richtig und wurde danach zurückgedreht.
   *
   * Deshalb wird der Wunsch gemerkt und **nach dem Zeichnen** noch einmal
   * gesprungen. Er hält, bis nichts mehr im Ausgangskorb wartet: ohne Netz
   * steht dort erst ein Eintrag, der später gegen die echte Nachricht getauscht
   * wird -- und dieser Tausch ist wieder ein Zeichnen.
   */
  const willAnsEnde = useRef(false);

  function nachUntenSpringen() {
    willAnsEnde.current = true;
    springen();
  }

  // **Die Zahl der Nachrichten, nicht die gefilterte Liste.** Die steht erst
  // weit unten, hinter den frühen Rückgaben -- ein Hook darf dort nicht mehr
  // stehen. Für „es ist etwas dazugekommen" tut es diese Zahl genauso.
  const anzahl = messages.data?.messages.length ?? 0;

  useEffect(() => {
    if (!willAnsEnde.current) return;
    springen();
    // Erst wenn nichts mehr wartet, ist der Vorgang vorbei.
    if (wartende.length === 0) willAnsEnde.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anzahl, wartende.length]);

  // Beim Wechsel des Chats ist der Wunsch hinfällig -- sonst spränge der neue
  // Verlauf, weil im alten etwas gesendet wurde.
  useEffect(() => {
    willAnsEnde.current = false;
    // Dasselbe für den Lesestand und den einen Sprung: ein anderer Verlauf
    // ist ein neues Öffnen, auch wenn das Bauteil stehen bleibt (#35).
    standBeimOeffnen.current = null;
    gesprungen.current = false;
    // Auch die Suche nach der Kante beginnt von vorn -- sie gehört dem Öffnen.
    kanteSeiten.current = 0;
    setKanteAufgegeben(false);
  }, [tripId, withUser]);

  /**
   * Zur bezogenen Nachricht springen.
   *
   * Steht sie schon im Verlauf, geht es sofort dorthin, und sie leuchtet kurz
   * auf -- ohne das weiß niemand, wo man gelandet ist.
   *
   * Steht sie nicht da, wird **einmal** nachgeladen. Sich seitenweise rückwärts
   * durch einen Sommer zu laden wäre kein Sprung mehr, sondern eine Reise; wer
   * so weit zurück will, blättert selbst.
   */
  function springeZu(messageId: number, nachgeladen = false) {
    const ziel = document.getElementById(`msg-${messageId}`);
    if (ziel !== null) {
      ziel.scrollIntoView({ block: "center", behavior: "smooth" });
      ziel.classList.add("bubble-row--gefunden");
      window.setTimeout(
        () => ziel.classList.remove("bubble-row--gefunden"),
        1600,
      );
      return;
    }

    const cursor = messages.data?.next_before ?? null;
    if (nachgeladen || cursor === null || older.isPending) return;
    older.mutate(cursor, {
      // Erst nach dem Zeichnen suchen: vorher gibt es das Element noch nicht.
      onSuccess: () => window.setTimeout(() => springeZu(messageId, true), 60),
    });
  }

  /**
   * Wie weit beim **Öffnen** gelesen war (#35).
   *
   * Aus dem Betrieb: „Bei Abrufen des Chats an die Stelle der Nachrichten
   * blättern, die nach der letzten Online-Zeit waren [...] wenn der Benutzer
   * schon aktiv ist, nicht selbständig scrollen."
   *
   * **Die Frage „wie erkennen wir ein neues Aufrufen?" beantwortet das
   * Bauteil selbst:** ein Öffnen ist ein Aufbau. Wer im Chat bleibt, baut
   * nichts neu auf, und dieser Merker füllt sich genau einmal.
   *
   * **Er ist vor dem Melden gefüllt, und zwar von selbst.** Der Lesestand wird
   * beim Öffnen sofort fortgeschrieben; käme die Meldung zuerst, gäbe es nie
   * eine ungelesene Nachricht zu sehen. Hier stand deshalb eine Sperre am
   * Melden -- **die Gegenprobe hat ihn widerlegt**: gemerkt wird beim
   * *Zeichnen*, gemeldet in einem *Effekt*, und Effekte laufen nach dem
   * Zeichnen. Die Reihenfolge liegt in der Bauart, nicht in einer Bedingung.
   * Eine Sperre, den nichts braucht, sagt dem nächsten Leser das Gegenteil.
   *
   * **Genommen wird der erste Wert, den dieser Aufbau zu sehen bekommt** --
   * auch der aus dem Zwischenspeicher. Hier stand `isFetchedAfterMount`, mit
   * der Überlegung, aus dem Speicher käme ein zu alter Stand; gemessen war
   * das Gegenteil: bei einem Klick aus der Liste liegt der Verlauf im Speicher,
   * die Meldung des Lesens ging **vor** der ersten frischen Antwort hinaus,
   * und der Merker blieb leer -- keine Trennlinie, obwohl drei Nachrichten
   * ungelesen waren.
   *
   * Der gespeicherte Stand ist dabei kein schlechterer: er ist die Antwort
   * vom letzten Öffnen desselben Verlaufs, also genau das, was damals gelesen
   * war. Zu *hoch* kann er nicht sein.
   */
  const standBeimOeffnen = useRef<number | null>(null);
  if (standBeimOeffnen.current === null && messages.data !== undefined) {
    standBeimOeffnen.current = messages.data.last_read_id;
  }

  /**
   * Die erste ungelesene Nachricht — oder `null`.
   *
   * `list` läuft von neu nach alt; die *erste* ungelesene im Lesesinn ist
   * also die **letzte** ungelesene in dieser Reihenfolge. Gesucht wird die
   * Kante: eine ungelesene, deren älterer Nachbar schon gelesen war.
   *
   * **Ohne sichtbare Kante keine Linie.** Ist die älteste geladene selbst
   * ungelesen und gibt es noch ältere zum Nachladen, wissen wir nicht, wo der
   * Verlauf umschlägt — eine Linie stünde dann irgendwo mitten im Ungelesenen
   * und behauptete eine Grenze, die woanders liegt. Nur wenn es gar nichts
   * Älteres mehr gibt, ist die älteste geladene wirklich die erste.
   *
   * **Daraus wurde aber „dann eben gar nichts", und das war der Fehler.** Aus
   * dem Betrieb: „wenn neue Nachrichten in einem älteren Abschnitt sind wird
   * nicht dorthin gesprungen, sondern ans Ende und man muss ältere Nachrichten
   * manuell nachladen." Genau dieser Fall — mehr Ungelesenes, als auf eine
   * Seite passt. Die Kante liegt dann nicht nirgends, sie liegt **weiter
   * oben**; gesucht wird sie jetzt dort (`kanteFehlt` weiter unten), statt
   * aufzugeben.
   *
   * Fremde Nachrichten zählen, eigene nicht: was man selbst geschrieben hat,
   * hat man nicht zu lesen versäumt.
   */
  const ersteUngelesene = (() => {
    const bis = standBeimOeffnen.current;
    if (bis === null) return null;
    // **Aus `messages.data`, nicht aus `list`.** Die gefilterte Liste steht
    // erst hinter den frühen Rückgaben, und ein Hook darf dort nicht mehr
    // stehen -- dieselbe Überlegung wie bei `anzahl` weiter oben. Gelöschte
    // werden hier gleich mit übersprungen; sie stehen im Verlauf nicht.
    const alle = (messages.data?.messages ?? []).filter((m) => !m.deleted);
    const nochAeltere = messages.data?.next_before != null;
    for (let i = 0; i < alle.length; i++) {
      const m = alle[i];
      if (m === undefined || m.own || m.id <= bis) continue;
      const aelter = alle[i + 1];
      if (aelter !== undefined && aelter.id <= bis) return m.id;
      if (aelter === undefined && !nochAeltere) return m.id;
    }
    return null;
  })();

  /**
   * **Die Kante liegt weiter oben, nicht nirgends.**
   *
   * Wahr, solange die älteste geladene Nachricht selbst ungelesen ist und es
   * noch ältere gibt: dann ist der Umschlagpunkt eine Seite weiter oben, und
   * `ersteUngelesene` kann ihn von hier aus nicht sehen.
   */
  const kanteFehlt = (() => {
    const bis = standBeimOeffnen.current;
    if (bis === null || ersteUngelesene !== null) return false;
    if (messages.data?.next_before == null) return false;
    const alle = (messages.data?.messages ?? []).filter((m) => !m.deleted);
    const aelteste = alle[alle.length - 1];
    return aelteste !== undefined && !aelteste.own && aelteste.id > bis;
  })();

  /**
   * Wie weit rückwärts gesucht wird, bevor es dabei bleibt.
   *
   * Fünf Seiten, dieselbe Zahl und derselbe Grund wie `LUECKE_MAX_SEITEN` in
   * `hooks/queries.ts`: wer mehr als zweihundert Nachrichten ungelesen hat,
   * war nicht kurz weg. Ohne Grenze wäre es eine Schleife über den ganzen
   * Verlauf — und die liefe ausgerechnet dann lange, wenn ohnehin viel
   * nachzuladen ist.
   *
   * **Wird die Grenze erreicht, bleibt der Verlauf stehen, wo er steht**, und
   * sagt es über dem Knopf. Stillschweigend ans Ende zu springen war der alte
   * Zustand und war der Befund.
   */
  const KANTE_MAX_SEITEN = 5;
  const kanteSeiten = useRef(0);
  const [kanteAufgegeben, setKanteAufgegeben] = useState(false);

  useEffect(() => {
    if (!kanteFehlt || gesprungen.current || older.isPending) return;
    const cursor = messages.data?.next_before ?? null;
    if (cursor === null) return;
    if (kanteSeiten.current >= KANTE_MAX_SEITEN) {
      setKanteAufgegeben(true);
      return;
    }
    kanteSeiten.current += 1;
    older.mutate(cursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kanteFehlt, older.isPending, messages.data?.next_before]);

  /**
   * Gesprungen wird **einmal** je Öffnen.
   *
   * Ein `useRef` und kein Zustand: es wird nirgends gezeichnet, und ein
   * erneutes Zeichnen wegen einer neu eintreffenden Nachricht darf den Sprung
   * nicht wiederholen -- sonst risse es einem den Verlauf unter der Hand weg,
   * während man liest. Genau das steht im Issue: „wenn der Benutzer schon
   * aktiv ist, nicht selbständig scrollen."
   */
  const gesprungen = useRef(false);
  useEffect(() => {
    if (gesprungen.current || ersteUngelesene === null) return;
    const ziel = document.getElementById(`msg-${ersteUngelesene}`);
    if (ziel === null) return;
    gesprungen.current = true;
    ziel.scrollIntoView({ block: "center" });
  }, [ersteUngelesene]);

  /**
   * **`?m=` — der Beitrag, der geklingelt hat.**
   *
   * Aus dem Betrieb: „Klick auf die Benachrichtigung führt aber nicht zur
   * Abstimmung oder zumindest zum richtigen Chat. → das sollte generell bei
   * Benachrichtigung funktionieren, dass man im richtigen Chat bzw. der
   * Nachricht landet."
   *
   * Der Chat allein ist die halbe Auskunft: wer nach zwanzig Beiträgen
   * hineinkommt, sucht den, der geklingelt hat. `springeZu()` kann das längst
   * — samt Nachladen, wenn der Beitrag noch nicht geladen ist; ihm fehlte nur
   * die Nummer.
   *
   * **Einmal je Aufbau, und danach nie wieder.** Ein zweiter Sprung risse
   * einem den Verlauf unter der Hand weg — dieselbe Regel wie beim Sprung zur
   * ersten ungelesenen Nachricht darüber. Und er wartet, bis die Liste
   * wirklich da ist: vorher gibt es das Element nicht, und `springeZu()`
   * lüde eine Seite nach, die schon unterwegs ist.
   */
  const angesprungen = useRef(false);
  const gesucht = Number(new URLSearchParams(location.search).get("m") ?? "");
  useEffect(() => {
    if (angesprungen.current || !Number.isInteger(gesucht) || gesucht <= 0) return;
    if (messages.data === undefined) return;
    angesprungen.current = true;
    springeZu(gesucht);
    // `springeZu` haengt am geladenen Verlauf und wird bei jedem Zeichnen neu
    // gebaut; der Zettel darueber sorgt dafuer, dass es trotzdem einmal bleibt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gesucht, messages.data === undefined]);

  const newest = messages.data?.messages[0]?.id;
  useEffect(() => {
    // Wer den Chat offen hat, hat gelesen. Ausgelöst wird das an der neuesten
    // Nachricht, nicht bei jedem Rendern -- sonst schriebe jedes Scrollen.
    //
    if (newest !== undefined) markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, withUser, newest]);

  // Ein fehlgeschlagener Abruf darf nicht wie ein leerer Chat aussehen --
  // sonst wirkt eine verweigerte Berechtigung wie „hier steht eben nichts“.
  //
  // **Ein Netzfehler gehört aber nicht dazu.** Aus dem Betrieb: im Flugmodus
  // stand hier „Dieser Chat lässt sich nicht öffnen", und damit war auch die
  // Eingabezeile weg -- also ausgerechnet der Weg in den Ausgangskorb, den es
  // für genau diesen Fall gibt. Ohne Empfang bleibt der Chat offen: was
  // geladen ist, bleibt lesbar, und schreiben geht weiter.
  const failure = trip.error ?? messages.error;

  // Eine Reise, die es nicht mehr gibt, ist keine Meldung wert -- nur der Weg
  // zurück.
  //
  // Aus dem Betrieb, auf dem iPhone: die Web-App nimmt beim Öffnen die zuletzt
  // besuchte Seite wieder auf, und die zeigte auf eine gelöschte Reise. Beim
  // Start stand dann „Dieser Chat lässt sich nicht öffnen" — für etwas, das
  // niemand angeklickt hatte. Wer die Reise verlassen hat oder aus ihr
  // entfernt wurde, bekommt vom Server denselben Bescheid; auch dann gehört er
  // heraus und nicht in eine Sackgasse. Wohin, entscheidet dann der
  // Startbereich -- mit gelöschter Erinnerung ist das der oberste Chat.
  //
  // Alles andere (ein Serverfehler etwa) bleibt stehen und wird gesagt: dazu
  /**
   * Wie groß die Fläche hinter dem Verlauf gerade ist.
   *
   * **Gemessen und nicht geraten**: `grundStil` rechnet die Bildgröße in
   * Bildpunkten aus, und dieselbe Reise steht am iPhone in einem hochkanten
   * und am iPad in einem fast quadratischen Kasten. Ein `ResizeObserver`, weil
   * sich das beim Drehen und beim Aufgehen der Tastatur ändert.
   *
   * **Diese drei standen weiter unten — hinter den Ausstiegen —, und genau
   * daran ging die Anwendung kaputt.**
   *
   * Aus dem Betrieb: „kurze Anzeige: wird geladen, dann nur ein weißer
   * Bildschirm", am iPhone in der Web-App, in Safari und am iPad nicht.
   * Gefunden hat es die Fehlergrenze aus `0.20.1` beim ersten Versuch, sie zu
   * messen: **React-Fehler #300, „rendered fewer hooks than expected"**.
   *
   * Der Ablauf: der erste Durchlauf lädt und ruft **alle** Hooks. Scheitert
   * die Abfrage, nimmt der zweite den Ausstieg weiter unten — und die Hooks
   * dahinter laufen nicht mehr. React zählt weniger als beim Mal davor, wirft,
   * und ohne Fehlergrenze bleibt das leere Wurzelelement. **Weiß.**
   *
   * Dass es sprunghaft auftrat, passt dazu: es braucht eine gescheiterte
   * Abfrage, und das Telefon macht beim Start als einziges Gerät gleich einen
   * Chat auf (`lib/letzterChat.ts`) — das iPad beginnt bei der Übersicht.
   *
   * **Die Regel, aus der es folgt, ist keine Feinheit:** Hooks stehen vor
   * jedem `return`. Wer einen Ausstieg einbaut, schiebt damit alles darunter
   * hinter eine Bedingung.
   */
  const flaeche = useRef<HTMLDivElement>(null);
  const [grundflaeche, setGrundflaeche] = useState({ breite: 0, hoehe: 0 });
  useEffect(() => {
    const el = flaeche.current;
    if (el === null) return;
    const messe = () =>
      setGrundflaeche({ breite: el.clientWidth, hoehe: el.clientHeight });
    messe();
    const wache = new ResizeObserver(messe);
    wache.observe(el);
    return () => wache.disconnect();
  }, []);

  // gibt es nichts Besseres zu tun, und stillschweigend zu verschwinden wäre
  // die schlechtere Auskunft.
  const verschwunden = failure instanceof ApiError && failure.status === 404;
  useEffect(() => {
    if (verschwunden) vergissOrt();
  }, [verschwunden]);
  if (verschwunden) return <Navigate to="/" replace />;

  if (failure && !ohneNetz(failure)) {
    return (
      <div className="empty">
        <p className="empty__title">{t.chat.nichtZuOeffnen}</p>
        <p>{failure.message}</p>
        <Link className="button button--quiet" to="/">
          {t.chat.zurueckZurListe}
        </Link>
      </div>
    );
  }

  /**
   * Der Fehlschlag beim Laden des Verlaufs — **oder `null`**.
   *
   * **Gefragt wird nach `failureReason` und nicht nach `isError`**, und das ist
   * der Kern der Meldung aus dem Betrieb. Die Abfrage traegt
   * `refetchInterval` und wiederholt sich; ein Serverfehler bringt sie damit
   * nie in den Endzustand „fehlgeschlagen" -- gemessen: siebenunddreissig
   * Versuche in achtundvierzig Sekunden, und `isPending` blieb dabei die ganze
   * Zeit wahr. Eine Meldung, die an `isError` haengt, kommt in diesem Fall
   * **nie**.
   *
   * `failureReason` traegt den letzten Fehlschlag, auch waehrend noch
   * weiterversucht wird. Genau das ist die ehrliche Auskunft: es wurde
   * versucht, es kam nichts, und weiter versucht wird trotzdem.
   *
   * **Erst nach den Wiederholungen**, nicht beim ersten Zucken: ein Aufblitzen
   * bei jedem kurzen Ruckler waere schlimmer als das Warten.
   *
   * **Und die Zahl ist zwei, nicht drei.** Die Regel in `main.tsx` gibt auf,
   * sobald `failureCount < 2` nicht mehr gilt -- gezaehlt wird also 1, dann 2,
   * und dann ist Schluss. Hier stand erst `>= 3`; die Schwelle wurde nie
   * erreicht, und die Meldung kam nie. Gemessen, nicht ueberlegt.
   *
   * **Und ohne Netz gilt es nicht.** Dafuer gibt es den eigenen Satz weiter
   * unten -- dort ist die Verbindung weg, hier hat der Server nein gesagt.
   */
  const ladefehler =
    messages.data === undefined
    && messages.failureCount >= 2
    && messages.failureReason !== null
    && !ohneNetz(messages.failureReason)
      ? messages.failureReason
      : null;

  const partner = members.data?.find((m) => m.user.id === withUser);
  const partnerName = partner?.user.display_name;

  // Warum hier nichts zu schreiben ist -- oder null, wenn es geht.
  //
  // Beurteilt wird erst, wenn die Teilnehmerliste **da** ist: solange sie
  // lädt, ist „nicht gefunden" kein Befund, sondern ein Zwischenstand, und
  // „nicht mehr in dieser Reise" blitzte bei jedem Öffnen kurz auf.
  const stumm =
    withUser === undefined || !members.isSuccess
      ? null
      : grundOhneZeile(partner);

  // Und solange es noch offen ist, steht unten gar nichts. Eine Eingabezeile,
  // die eine Zehntelsekunde später verschwindet, ist dieselbe Verwirrung in
  // klein -- lieber einen Augenblick nichts als kurz ein falsches Versprechen.
  const wartetAufAuskunft = withUser !== undefined && members.isPending;

  // Ohne Netz weiß die Reise selbst nichts mehr zu sagen -- die Chatliste
  // daneben aber schon: sie trägt Titel und Namen und liegt im
  // Zwischenspeicher. Ein Kopf mit „…" wäre schlechter als einer aus zweiter
  // Hand.
  const ausListe = threads.data?.find(
    (t) => t.trip_id === tripId && (t.with_user?.id ?? undefined) === withUser,
  );

  const title = withUser
    ? (partnerName ?? ausListe?.with_user?.display_name ?? "Antworten")
    : (trip.data?.title ?? ausListe?.trip_title ?? "…");

  // Ohne Angaben zur Reise wird nichts behauptet: „Reisebericht von" mit
  // leerem Namen dahinter sah aus wie ein Fehler und war nur fehlender Empfang.
  // Dass kein Netz da ist, sagt der Streifen über der Eingabezeile -- hier
  // stünde es ein zweites Mal.
  const zweck =
    trip.data === undefined
      ? ""
      : withUser
        ? `Antworten in „${trip.data.title}“ – nur für dich sichtbar`
        : trip.data?.role === "sender"
          ? // **„Travel Broadcast" statt „Rundbrief"** — so aus dem Betrieb
            // gewünscht. Es klingt nach Serienbrief und Verwaltung; das Ding
            // heißt so, wie die Anwendung heißt.
            //
            // **Und das gilt inzwischen auch für Kommentare und Doku.** Die
            // Oberfläche sagte es längst richtig, im Code lebte das alte Wort
            // vierunddreißig Mal weiter. Diese Stelle hier ist die einzige,
            // an der es stehen bleibt — sie ist der Beleg für die
            // Entscheidung. Sonst heißt es **Beitrag** (die Nachricht) oder
            // **an alle** (die Reichweite); die Regel steht in `CLAUDE.md`.
            `Travel Broadcast an ${(trip.data.member_count ?? 1) - 1} Empfänger` +
            // Wer wartet, wartet auf eine Entscheidung -- und die trifft niemand,
            // der nichts davon weiß. Die Reiseseite ist einen Tipp entfernt.
            (trip.data.pending_requests > 0
              ? ` · ${trip.data.pending_requests} ${trip.data.pending_requests === 1 ? "Anfrage" : "Anfragen"}`
              : "")
          : `Reisebericht von ${trip.data?.owner.display_name ?? ""}`;

  // Die Beschreibung stand bisher nur auf der Infoseite -- also dort, wo man
  // sie beim Lesen nie sieht. Sie gehört in den Kopf des Chats, denn sie sagt,
  // worum es in dieser Reise geht.
  //
  // Eigene Zeile statt angehängt: hinter „Travel Broadcast an 5 Empfänger" wäre sie
  // auf einem Telefon nach drei Wörtern zu Ende. So bekommt sie zwei Zeilen,
  // und die Angaben darüber -- vor allem eine wartende Anfrage -- bleiben
  // unangetastet. Der Kopf wächst nur dort, wo es überhaupt etwas zu sagen
  // gibt.
  const beschreibung = withUser ? "" : (trip.data?.description ?? "").trim();

  /**
   * **Eine gelöschte Nachricht steht nicht mehr da — auch nicht als Lücke.**
   *
   * Hier stand einmal „Diese Nachricht wurde gelöscht." Aus dem Betrieb
   * gewünscht: löschen heißt weg, nicht „hier war etwas". Ein Grabstein sagt
   * mehr über den Vorgang als das, was gelöscht wurde.
   *
   * **An der Datenbank ändert das nichts.** Der Server führt die Zeile
   * weiter mit `deleted_at`, liefert sie weiter aus und hält damit Zählung,
   * Lesestände und Zitate zusammen. Nur angezeigt wird sie nicht mehr.
   */
  const list = (messages.data?.messages ?? []).filter((m) => !m.deleted);

  /**
   * Farbe und Bild für den Hintergrund — oder `null`, wenn nichts gesetzt ist.
   *
   * Beides kommt von der Reise; eingestellt hat es der Reisende. Der Deckgrad
   * liegt am Bild und nicht an der Farbe: die Farbe ist das, worauf das Bild
   * liegt, und soll deckend bleiben.
   */
  const grund = trip.data?.chat_bg;
  const hintergrund =
    grund && (grund.color !== null || grund.url !== null)
      ? {
          aussen: grund.color !== null ? { background: grund.color } : {},
          bild:
            grund.url !== null
              ? {
                  backgroundImage: `url(${JSON.stringify(grund.url)})`,
                  opacity: Math.max(0, Math.min(100, grund.opacity)) / 100,
                  // **Der gewählte Ausschnitt** (#51) -- gerechnet, nicht als
                  // `cover`: nur so kann das Bild auch kleiner sein als die
                  // Fläche und das ganze Motiv auf der Farbe stehen.
                  ...grundStil(
                    ausschnittAus(grund),
                    grundflaeche,
                    { breite: grund.width, hoehe: grund.height },
                  ),
                }
              : null,
        }
      : null;
  const olderCursor = messages.data?.next_before ?? null;

  return (
    <>
      <header className="bar">
        <Link className="icon-button" to="/" aria-label={t.chat.zurueckZurListe}>
          <BackIcon />
        </Link>
        <div className="bar__grow">
          <h2 className="bar__title">{title}</h2>
          <p className="bar__subtitle">{zweck}</p>
          {/* `title`: bei mehr als zwei Zeilen lässt sie sich wenigstens im
              Vorbeigehen lesen -- ganz steht sie auf der Infoseite. */}
          {/* **Auf dem Telefon fällt sie weg** (`bar__desc` ist dort
              `display: none`). Der Schirm ist schmal, und die Beschreibung
              steht ganz auf der Infoseite hinter dem „i" — zwei Zeilen
              Erklärung über einem Chat sind dort der schlechtere Handel. */}
          {beschreibung !== "" && (
            <p className="bar__desc" title={beschreibung}>
              {beschreibung}
            </p>
          )}
        </div>
        {/* Nur beim Reisenden und nur im Reise-Chat: siehe `Antworten.tsx`. */}
        {withUser === undefined && trip.data?.role === "sender" && (
          <AntwortenKnopf tripId={tripId} />
        )}
        <Link
          className="icon-button"
          to={`/t/${tripId}/info`}
          aria-label={t.chat.angabenZurReise}
        >
          <InfoIcon />
        </Link>
      </header>

      {/* Nur im Reise-Chat, nicht im Antwortverlauf eines Empfängers: die
          Karte gehört zur Reise, und ein Umschalter „Chat · Karte" über einem
          Zweiergespräch verspräche einen Wechsel, den es dort nicht gibt. */}
      {withUser === undefined && (
        <Reiseumschalter
          tripId={tripId}
          aktiv="chat"
          darfBearbeiten={trip.data?.role === "sender"}
        />
      )}

      {/* column-reverse: die neueste Nachricht steht unten, und der Browser
          hält den Blick von selbst dort, wenn eine dazukommt. */}
      {/* **Der Hintergrund liegt hinter dem Verlauf, nicht darin.**
          Ein eigenes Element und kein `background` am Verlauf selbst: der
          Deckgrad gilt dem Bild, nicht dem Text darüber. Läge das Bild als
          Hintergrund am Verlauf, müsste man die Schrift wieder heraufholen —
          und jede Sprechblase hätte ihre eigene Durchsicht.
          `aria-hidden`, denn es sagt nichts. */}
      {/* **Ein eigener Kasten um beides.** Der Hintergrund lag zuvor auf
          `inset: 0` der ganzen Seite und malte damit über die Kopfzeile und
          über die Eingabezeile — beide undurchsichtig und trotzdem weg. Hier
          reicht er nur noch so weit wie der Verlauf, den er hinterlegt. */}
      <div className="chatflaeche" ref={flaeche}>
        {hintergrund !== null && (
          <div
            className="chatgrund"
            aria-hidden="true"
            style={hintergrund.aussen}
          >
            {hintergrund.bild !== null && (
              <div className="chatgrund__bild" style={hintergrund.bild} />
            )}
          </div>
        )}

        <div className="timeline" ref={verlauf}>
          {messages.isPending && <p className="empty">{t.chat.wirdGeladen}</p>}

          {/*
            **Ein Fehlschlag beim Laden ist kein leerer Chat** (aus dem
            Betrieb).

            Hier stand nur `isPending` und danach der leere Fall -- und ein
            Serverfehler landete damit in „Noch nichts geschrieben". Das ist
            keine fehlende Auskunft, sondern eine **falsche**: die App
            behauptete, der Verlauf sei leer, obwohl sie ihn gar nicht
            bekommen hat. Gemeldet wurde es so: „während der Startbildschirm
            gezeigt wurde, habe ich auf Karte gedrückt, dann wurde angezeigt
            'Reise gibt es nicht' … der Fehler sollte dann auch im Chat
            abgefangen werden."

            **Ohne Netz bleibt beim eigenen Satz** -- der steht weiter unten
            und sagt etwas anderes: dort ist die Verbindung weg, hier hat der
            Server geantwortet und nein gesagt.

            **Und es gibt einen Weg heraus.** Ein Fehler ohne Knopf ist eine
            Sackgasse; auf der Karte war genau das der Unterschied.
          */}
          {ladefehler !== null && (
              <div className="empty" role="alert" data-schluessel="ladefehler">
                <p className="empty__title">{t.chat.ladefehler}</p>
                <p>{t.chat.ladefehlerText}</p>
                {ladefehler instanceof ApiError && (
                  <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    {t.chat.ladefehlerNummer(ladefehler.status)}
                  </p>
                )}
                <button
                  className="button button--outline"
                  type="button"
                  onClick={() => void messages.refetch()}
                >
                  {t.chat.nochmal}
                </button>
              </div>
            )}

          {!messages.isPending &&
            ladefehler === null &&
            list.length === 0 &&
            wartende.length === 0 && (
              <div className="empty" data-schluessel="leer">
                {/* „Noch nichts geschrieben" wäre hier eine Behauptung über den
                Verlauf -- ohne Netz weiß die App darüber gerade nichts.

                **Der Schlüssel steht im DOM für den Prüfstand**: er muss
                messen können, dass dieser Block bei einem Fehlschlag gerade
                NICHT dasteht, ohne sich am Wortlaut festzuhalten. */}
                {messages.data === undefined && ohneNetz(messages.error) ? (
                  <>
                    <p className="empty__title">{t.chat.keinNetz}</p>
                    <p>{t.chat.keinNetzText}</p>
                  </>
                ) : (
                  <>
                    <p className="empty__title">{t.chat.nochNichts}</p>
                    <p>
                      {withUser
                        ? t.chat.nochKeineAntwort(partnerName ?? t.chat.diesePerson)
                        : trip.data?.role === "sender"
                          ? t.chat.schreibDieErste
                          : t.chat.wartetAufReisenden}
                    </p>
                  </>
                )}
              </div>
            )}

          {/* Vor der Liste, weil die Zeitleiste umgekehrt läuft (column-reverse):
            was hier steht, erscheint unten -- und dort gehört das hin, was als
            Nächstes rausgeht. */}
          {[...wartende].reverse().map((eintrag) => (
            <Wartend key={`wartet-${eintrag.key}`} eintrag={eintrag} />
          ))}

          {list.map((message, index) => {
            const next = list[index + 1];
            const startsDay =
              !next || !sameDay(message.created_at, next.created_at);
            return (
              <div key={message.id} style={{ display: "contents" }}>
                <Bubble
                  message={message}
                  tripId={tripId}
                  withUser={withUser}
                  // Für die Kärtchen einer Freigabe: sie zeigen ein Datum, und
                  // ein Datum ohne Uhr ist die halbe Angabe.
                  reiseVersatz={trip.data?.tz_offset_min ?? null}
                  showAuthor={!message.own && Boolean(withUser)}
                  canReact={trip.data?.archived_at == null}
                  // Wer das liest, entscheidet über den Wortlaut, und nur diese
                  // Seite kennt beide Blickwinkel: der Mitleser soll nicht
                  // glauben, eine persönliche Nachricht gehe an alle -- und
                  // der Reisende sieht in ihrem Verlauf sonst nicht, was an alle
                  // ging und was nur an sie.
                  // Der Rückfall nennt keine Person, sondern „diese Person":
                  // solange der Name noch nicht da ist, wäre jedes Fürwort
                  // geraten -- und geraten heißt hier: bei jedem zweiten falsch.
                  directLabel={
                    withUser
                      ? t.chat.nurAn(partnerName ?? t.chat.dieserPerson)
                      : t.chat.nurAnDich
                  }
                  markiereBroadcast={trip.data?.mark_broadcast ?? false}
                  onReply={(ziel, mediaId) =>
                    setBezug({ message: ziel, mediaId })
                  }
                  onJump={springeZu}
                  /*
                   * **Nur an eigenen, nicht gelöschten Nachrichten** (#30).
                   * Der Kreis ist eine Bequemlichkeit; die Sperre steht im
                   * Server und bleibt dort.
                   */
                  auswahl={
                    auswahl !== null && message.own && !message.deleted
                      ? {
                          gewaehlt: auswahl.has(message.id),
                          waehle: () =>
                            setAuswahl((alt) => {
                              const neu = new Set(alt ?? []);
                              if (neu.has(message.id)) neu.delete(message.id);
                              else neu.add(message.id);
                              return neu;
                            }),
                        }
                      : undefined
                  }
                  onLoeschmodus={(id) => {
                    setLoeschbefund(null);
                    setAuswahl(new Set([id]));
                  }}
                  // Nur der Reisende, und nur im Reise-Chat: was in einem
                  // Antwortverlauf steht, ist die Antwort eines Einzelnen und
                  // gehört nicht auf eine Karte, die alle sehen.
                  onAufDieKarte={
                    trip.data?.role === "sender" && withUser === undefined
                      ? (ziel) => {
                          merkeUebergabe({
                            tripId,
                            // **Der Beitrag ist der Text, nicht der Titel.**
                            //
                            // Hier stand die erste Zeile als Titel und der Rest
                            // als Text -- „ein Reisebericht fängt fast immer mit
                            // einer an". Tut er nicht: die meisten Beiträge sind
                            // ein einziger Absatz, und der landete dann
                            // vollständig in der Titelzeile, während der Text
                            // leer blieb. Aus dem Betrieb gemeldet.
                            //
                            // Der Titel bleibt leer und füllt sich mit dem
                            // Ortsnamen, sobald er da ist -- derselbe Weg wie
                            // bei „Aus Foto/Video".
                            title: "",
                            body: ziel.body,
                            at: ziel.created_at,
                          });
                          // In die Werkbank, nicht in die Ansicht: ein Entwurf
                          // ist Werkzeug, und das steht seit der Trennung nur
                          // im Reiter „Bearbeiten".
                          navigate(`/t/${tripId}/karte/bearbeiten`);
                        }
                      : undefined
                  }
                />
                {startsDay && (
                  <div className="day-divider">
                    {dayLabel(message.created_at)}
                  </div>
                )}
                {/* **Die Trennlinie steht nach der Blase** (#35).

                    Der Verlauf läuft umgekehrt (`column-reverse`): was hier
                    unter der Blase steht, erscheint im Bild **über** ihr --
                    genau wie die Datumszeile eine Zeile höher. Sie markiert
                    damit den Anfang des Ungelesenen und nicht sein Ende. */}
                {message.id === ersteUngelesene && (
                  <div className="neu-trenner">
                    <span>{t.chat.neueNachrichten}</span>
                  </div>
                )}
              </div>
            );
          })}

          {/* **Keine stille Absage.** Ist die Suche nach dem Umschlagpunkt an
              ihrer Grenze angekommen, steht das da -- sonst sähe es aus wie
              ein Verlauf ohne Ungelesenes, und genau so las sich der Befund
              aus dem Betrieb. */}
          {kanteAufgegeben && olderCursor != null && (
            <p className="notice" style={{ margin: "10px 14px" }}>
              Weiter oben liegt noch Ungelesenes — mehr, als sich auf einmal
              nachladen lässt.
            </p>
          )}

          {olderCursor != null && (
            <button
              type="button"
              className="button button--outline"
              style={{ margin: "10px auto", width: "auto" }}
              disabled={older.isPending}
              onClick={() => older.mutate(olderCursor)}
            >
              {older.isPending ? t.chat.wirdGeladen : t.chat.aeltere}
            </button>
          )}
        </div>
      </div>

      {/* Hier stand lange der Satz „Schreiben kannst du im Reise-Chat an
          alle." -- der Verlauf war einseitig: sie konnte ihm schreiben, er ihr
          nicht. Jetzt geht auch die andere Richtung, und was hier hinausgeht,
          sieht ausschließlich diese eine Person. */}
      {/* Unmittelbar über der Eingabezeile: dort steht er neben der Handlung,
          um die es geht, und liegt in keinem Browser unter einer Leiste. Nicht
          im Composer selbst, denn wo statt des Feldes ein Grund steht
          (abgeschlossene Reise, pausierte Empfängerin), gehört er trotzdem
          hin. */}
      <Netzstreifen />

      {/* **Die Hinweiszeile ist weg.** Hier stand „Was du hier schreibst,
          sieht nur X." als eigener Absatz über der Eingabezeile. Aus dem
          Betrieb: „diese Hinweiszeile entfernen und durch ein Symbol
          ersetzen in der leeren Nachrichtenzeile". Sie steht jetzt als
          Sinnbild im leeren Feld und geht mit dem ersten Zeichen weg -- wie
          der Platzhalter, dessen Aussage sie ohnehin verdoppelt hat. */}

      {/*
        * **Die Leiste ersetzt die Eingabezeile, sie legt sich nicht darüber**
        * (#30). Zwei Leisten übereinander nehmen dem Verlauf auf 390 Punkten
        * die halbe Höhe — und schreiben kann man ohnehin nicht, während man
        * auswählt.
        */}
      {loeschbefund !== null && (
        <p className="auswahlleiste__hinweis" role="alert">
          {loeschbefund}
        </p>
      )}

      {auswahl !== null ? (
        <Auswahlleiste
          anzahl={auswahl.size}
          laeuft={loeschen.isPending}
          online={online}
          onAbbrechen={() => {
            setAuswahl(null);
            setLoeschbefund(null);
          }}
          onLoeschen={async () => {
            const ids = [...auswahl];
            if (
              !(await rueckfrage(
                `${nachrichtenWorte(ids.length)} löschen?`,
                "Löschen",
              ))
            ) {
              return;
            }
            const befund = await loeschen.mutateAsync(ids);
            if (befund.geblieben.length === 0) {
              setAuswahl(null);
              setLoeschbefund(null);
              return;
            }
            // **Keine stille Absage.** Was nicht durchkam, bleibt markiert,
            // und die Zahl daneben sagt, wie viel durchkam.
            setAuswahl(new Set(befund.geblieben));
            setLoeschbefund(
              `${befund.weg.length} von ${ids.length} gelöscht — der Rest blieb stehen.`,
            );
          }}
        />
      ) : !wartetAufAuskunft ? (
        <Composer
          tripId={tripId}
          withUser={withUser}
          bezug={bezug}
          onBezugWeg={() => setBezug(null)}
          onGesendet={nachUntenSpringen}
          disabled={trip.data?.archived_at != null || stumm !== null}
          // Die abgeschlossene Reise hat Vorrang: sie gilt für alle Verläufe,
          // und ein Grund, der nur diesen einen betrifft, wäre daneben falsch.
          disabledHint={
            trip.data?.archived_at != null ? undefined : (
              <>
                {stumm?.text.replace("{name}", partnerName ?? t.chat.diesePerson)}
                {stumm?.zurReise && (
                  <>
                    {" "}
                    <Link to={`/t/${tripId}/info`}>
                      {t.chat.aufDerReiseseite}
                    </Link>
                  </>
                )}
              </>
            )
          }
          // Von lang nach kurz — der Composer nimmt die längste Fassung,
          // die in die Zeile passt. „Nachricht an alle Empfänger" wurde auf
          // dem Telefon abgeschnitten; abgeschnitten sagt weniger als kurz.
          // **Wer es sehen wird** -- dieselbe Aussage wie der Platzhalter
          // daneben, einmal als Bild. Ein Empfänger oder alle: der private
          // Verlauf und die Antwort eines Mitlesenden gehen an einen, der
          // Beitrag des Reisenden an alle.
          reichweite={
            withUser === undefined && trip.data?.role === 'sender' ? 'alle' : 'einer'
          }
          platzhalter={
            withUser
              ? t.chat.platzhalterAnEinen(partnerName ?? t.chat.dieserPerson)
              : trip.data === undefined
                ? t.chat.platzhalterUnbekannt
                : trip.data.role === "sender"
                  ? t.chat.platzhalterAnAlle
                  : t.chat.platzhalterAntwort(trip.data.owner.display_name)
          }
        />
      ) : null}
    </>
  );
}

/**
 * Warum an diesen Menschen gerade nichts geht — oder null, wenn es geht.
 *
 * Die Reihenfolge ist die der Endgültigkeit: wer gar nicht mehr dabei ist,
 * bleibt es; ein stillgelegtes Konto kann die Verwaltung wieder freigeben; ein
 * Stichtag ist ein Handgriff auf der Reiseseite. Genannt wird immer der
 * Zustand, der die Nachricht **zuerst** aufhält.
 *
 * `{name}` wird von der Seite ersetzt — hier steht der Name noch nicht fest,
 * weil er aus derselben Liste kommt, die auch diesen Zustand liefert.
 */
function grundOhneZeile(
  partner: Member | undefined,
): { text: string; zurReise: boolean } | null {
  if (partner === undefined) {
    return {
      text: WORTLAUT.chat.nichtMehrDabei,
      zurReise: false,
    };
  }
  // **Selbst ausgestiegen — und das musste hierher.** Seit die
  // Empfängerliste Ausgestiegene mitführt (damit sie nicht spurlos fehlen),
  // *findet* diese Seite den Partner wieder: ohne diesen Fall fiele er durch
  // alle Prüfungen, und die Eingabezeile stünde offen, obwohl niemand mehr
  // dahinter ist. Aus dem Betrieb gemeldet, einen Tag nach dem Umbau.
  if (partner.left === true) {
    return {
      text: WORTLAUT.chat.ausgestiegen,
      zurReise: false,
    };
  }
  if (partner.disabled) {
    return {
      text: WORTLAUT.chat.stillgelegt,
      zurReise: false,
    };
  }
  if (partner.cut_off) {
    return {
      text: WORTLAUT.chat.ausDemVerteiler,
      zurReise: true,
    };
  }
  return null;
}
